import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Page, Layout, Card, FormLayout, TextField, Select, Checkbox, BlockStack,
  Text, InlineStack, Banner, Spinner, Box, Badge, Toast, Button,
} from '@shopify/polaris';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getWheel, updateWheel, deleteWheel, verifyWheelImages,
  type Wheel, type WheelPayload,
} from '../api/wheels';
import { syncShopifyFitment, type SyncResponse } from '../api/shopify-sync';
import { recomputeMatches } from '../api/matches';
import { BoltPatternEditor } from '../components/BoltPatternEditor';
import { MatchSourceBadge } from '../components/ConfidenceBadge';
import { AuditLogPanel } from '../components/AuditLogPanel';
import { generateListing, type ListingResult } from '../api/listing';
import { getTagsForWheel } from '../api/collections-preview';
import { WheelImageGallery } from '../components/WheelImageGallery';
import {
  ListingSeoPanel, GoogleMerchantPanel, MetafieldsPanel, VariantsPanel,
} from '../components/ListingPanels';
import { dollars, priceSourceBadge, ago, hasImage, qtyCell, InstallSpeedBadge } from './WheelsList';
import { Divider } from '@shopify/polaris';

export function WheelDetail() {
  const { id } = useParams<{ id: string }>();
  const numericId = id ? parseInt(id, 10) : null;
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data: wheel, isLoading, error } = useQuery({
    queryKey: ['wheel', numericId],
    queryFn: () => getWheel(numericId!),
    enabled: numericId !== null,
  });

  const [form, setForm] = useState<Partial<Wheel> & { bolt_pattern_raw?: string }>({});
  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => {
    // Reset form state on every wheel id change — stops stale bp1/bp2 from a
    // previously-viewed wheel leaking into this one.
    setForm({});
  }, [numericId]);

  useEffect(() => {
    if (!wheel) return;
    // Two dedicated fields for BP 1 and BP 2 so dual-drilled wheels are easy
    // to see/edit. Strictly derived from the current wheel's child rows —
    // never from any prior form state. Single-pattern wheels get bp2 = ''.
    const raws = Array.isArray(wheel.bolt_patterns)
      ? wheel.bolt_patterns.map((b) => b.bolt_pattern_raw).filter(Boolean)
      : [];
    const bp1 = raws[0] ?? '';
    const bp2 = raws.length > 1 ? (raws[1] ?? '') : '';
    const rawJoined = [bp1, bp2].filter(Boolean).join('/');
    setForm({ ...wheel, bolt_pattern_raw: rawJoined, bp1, bp2 } as any);
  }, [wheel]);

  const save = useMutation({
    mutationFn: async (patch: WheelPayload) => updateWheel(numericId!, patch),
    onSuccess: () => {
      setToast('Saved');
      qc.invalidateQueries({ queryKey: ['wheel', numericId] });
      qc.invalidateQueries({ queryKey: ['wheels'] });
      qc.invalidateQueries({ queryKey: ['audit'] });
    },
  });

  const del = useMutation({
    mutationFn: () => deleteWheel(numericId!),
    onSuccess: () => { setToast('Archived'); navigate('/wheels'); },
  });

  const recompute = useMutation({
    mutationFn: () => recomputeMatches({ scope: 'wheel', id: numericId! }),
    onSuccess: (res) =>
      setToast(`Matches recomputed (inserted ${res.inserted ?? 0}, deleted ${res.deleted ?? 0})`),
    onError: (err: Error) => setToast(`Recompute failed: ${err.message}`),
  });

  const resync = useMutation<SyncResponse, Error, void>({
    mutationFn: async () => syncShopifyFitment({ ids: [numericId!] }, { force: true }),
    onSuccess: (res) => {
      const r = res.results[0];
      if (!r) { setToast('Re-sync: no result'); return; }
      if (r.status === 'error') setToast(`Re-sync error: ${r.error}`);
      else if (r.status === 'skipped_no_shopify_id') setToast('Skipped: wheel has no shopify_product_id');
      else setToast(`Re-synced: ${r.status} (${r.vehicle_count} vehicles)`);
    },
    onError: (err) => setToast(`Re-sync failed: ${err.message}`),
  });

  const verify = useMutation({
    mutationFn: (v: boolean) => verifyWheelImages(numericId!, v),
    onSuccess: (updated) => {
      setToast(updated.images_verified ? 'Images verified' : 'Verification cleared');
      qc.invalidateQueries({ queryKey: ['wheel', numericId] });
      qc.invalidateQueries({ queryKey: ['wheels'] });
    },
    onError: (err: Error) => setToast(`Verify failed: ${err.message}`),
  });

  // Listing generator (template-based today; AI-optional later via ANTHROPIC_API_KEY)
  const [listing, setListing] = useState<ListingResult | null>(null);
  const generate = useMutation<ListingResult, Error, void>({
    mutationFn: () => generateListing(numericId!),
    onSuccess: (data) => { setListing(data); setToast('Listing generated'); },
    onError: (err) => setToast(`Generate failed: ${err.message}`),
  });

  if (isLoading) return <Page title="Wheel"><Spinner /></Page>;
  if (error) return <Page title="Wheel"><Banner tone="critical">{(error as Error).message}</Banner></Page>;

  const w = form as Wheel & { bolt_pattern_raw?: string };
  const handle = <K extends keyof typeof form>(key: K) => (value: any) => setForm((p) => ({ ...p, [key]: value }));
  const handleNum = <K extends keyof typeof form>(key: K) => (value: string) =>
    setForm((p) => ({ ...p, [key]: value === '' ? null : Number(value) }));

  return (
    <>
      <Page
        title={`${w.brand ?? ''} ${w.model ?? ''}`.trim() || 'Wheel'}
        subtitle={w.sku}
        backAction={{ content: 'Wheels', onAction: () => navigate('/wheels') }}
        primaryAction={{
          content: 'Save changes',
          loading: save.isPending,
          onAction: () => save.mutate(buildPatch(form)),
        }}
        secondaryActions={[
          {
            content: 'Recompute matches',
            loading: recompute.isPending,
            onAction: () => recompute.mutate(),
            helpText: 'Re-run matching between this wheel and all active vehicles',
          },
          {
            content: 'Re-sync Shopify fitment',
            loading: resync.isPending,
            onAction: () => resync.mutate(),
            helpText: 'Rewrite fitment metafields on this wheel\'s Shopify product',
          },
          {
            content: 'Archive',
            destructive: true,
            loading: del.isPending,
            onAction: () => { if (confirm('Archive this wheel?')) del.mutate(); },
          },
        ]}
      >
        {save.error && (
          <Box paddingBlockEnd="400">
            <Banner tone="critical" title="Save failed">{(save.error as Error).message}</Banner>
          </Box>
        )}
        <Layout>
          <Layout.Section>
            <Card>
              <BlockStack gap="400">
                <Text as="h3" variant="headingMd">Identity</Text>
                <FormLayout>
                  <FormLayout.Group>
                    <TextField label="SKU" value={w.sku ?? ''} onChange={handle('sku')} autoComplete="off" />
                    <TextField label="Brand" value={w.brand ?? ''} onChange={handle('brand')} autoComplete="off" />
                    <TextField label="Model" value={w.model ?? ''} onChange={handle('model')} autoComplete="off" />
                  </FormLayout.Group>
                  <FormLayout.Group>
                    <TextField label="Finish" value={w.finish ?? ''} onChange={handle('finish')} autoComplete="off" />
                    <Select
                      label="Construction"
                      options={[
                        { label: '—', value: '' },
                        { label: 'Cast', value: 'Cast' },
                        { label: 'Forged', value: 'Forged' },
                        { label: 'Flow-Formed', value: 'Flow-Formed' },
                      ]}
                      value={w.construction ?? ''}
                      onChange={handle('construction')}
                    />
                    <Select
                      label="Position hint"
                      options={[
                        { label: '—', value: '' },
                        { label: 'Front', value: 'Front' },
                        { label: 'Rear', value: 'Rear' },
                        { label: 'Either', value: 'Either' },
                      ]}
                      value={w.position_hint ?? ''}
                      onChange={handle('position_hint')}
                    />
                  </FormLayout.Group>
                </FormLayout>
              </BlockStack>
            </Card>
          </Layout.Section>

          <Layout.Section>
            <Card>
              <BlockStack gap="400">
                <Text as="h3" variant="headingMd">Bolt patterns</Text>
                <FormLayout.Group>
                  <TextField
                    label="BP 1"
                    value={(w as any).bp1 ?? ''}
                    onChange={(v) => {
                      const bp2 = (w as any).bp2 ?? '';
                      const joined = [v, bp2].filter(Boolean).join('/');
                      setForm((f: any) => ({ ...f, bp1: v, bolt_pattern_raw: joined }));
                    }}
                    placeholder="6x135"
                    autoComplete="off"
                  />
                  <TextField
                    label="BP 2 (optional — for dual-drilled)"
                    value={(w as any).bp2 ?? ''}
                    onChange={(v) => {
                      const bp1 = (w as any).bp1 ?? '';
                      const joined = [bp1, v].filter(Boolean).join('/');
                      setForm((f: any) => ({ ...f, bp2: v, bolt_pattern_raw: joined }));
                    }}
                    placeholder="6x139.7"
                    autoComplete="off"
                  />
                </FormLayout.Group>
                <Text as="p" variant="bodySm" tone="subdued">
                  Leave BP 2 empty for single-pattern wheels. For dual-drilled wheels
                  both fields are preserved exactly — raw string becomes
                  "{'{BP1}/{BP2}'}".
                </Text>
                {(wheel?.bolt_patterns ?? []).length > 0 && (
                  <Box borderWidth="025" borderColor="border" borderRadius="200" padding="300">
                    <BlockStack gap="200">
                      <Text as="p" variant="bodySm" tone="subdued">Currently stored ({wheel!.bolt_patterns!.length})</Text>
                      {wheel!.bolt_patterns!.map((bp) => (
                        <InlineStack key={bp.id} gap="200" blockAlign="center">
                          <Text as="span" variant="bodySm" tone="subdued">{bp.bolt_pattern_raw}</Text>
                          <Text as="span" variant="bodySm" tone="subdued">→</Text>
                          <Badge tone="info">{bp.bolt_pattern_canonical}</Badge>
                          <MatchSourceBadge source={bp.match_source} />
                        </InlineStack>
                      ))}
                    </BlockStack>
                  </Box>
                )}
              </BlockStack>
            </Card>
          </Layout.Section>

          <Layout.Section>
            <Card>
              <BlockStack gap="400">
                <Text as="h3" variant="headingMd">Pricing</Text>
                <InlineStack gap="400" wrap>
                  <Box minWidth="140px">
                    <Text as="p" variant="bodySm" tone="subdued">Effective sell</Text>
                    <Text as="p" variant="headingLg">{dollars(w.effective_price_cents)}</Text>
                    <Box paddingBlockStart="100">{priceSourceBadge(w.price_source)}</Box>
                  </Box>
                  <Box minWidth="120px">
                    <Text as="p" variant="bodySm" tone="subdued">Cost</Text>
                    <Text as="p" variant="headingMd">{dollars(w.cost_cents)}</Text>
                  </Box>
                  <Box minWidth="120px">
                    <Text as="p" variant="bodySm" tone="subdued">MAP</Text>
                    <Text as="p" variant="headingMd">{dollars(w.map_price_cents)}</Text>
                  </Box>
                  <Box minWidth="120px">
                    <Text as="p" variant="bodySm" tone="subdued">Retail</Text>
                    <Text as="p" variant="headingMd">{dollars(w.retail_cents)}</Text>
                  </Box>
                  <Box minWidth="120px">
                    <Text as="p" variant="bodySm" tone="subdued">MSRP</Text>
                    <Text as="p" variant="headingMd">{dollars(w.msrp_cents)}</Text>
                  </Box>
                  <Box minWidth="100px">
                    <Text as="p" variant="bodySm" tone="subdued">Qty on hand</Text>
                    <Box paddingBlockStart="100">{qtyCell(w.quantity_on_hand)}</Box>
                  </Box>
                </InlineStack>
                <Text as="p" variant="bodySm" tone="subdued">
                  Sell price resolves in order: MAP → Retail → Cost × markup.
                  Source vendor fields (cost / MAP / retail / MSRP) are preserved
                  exactly as received — the fallback never overwrites them.
                </Text>
              </BlockStack>
            </Card>
          </Layout.Section>

          <Layout.Section>
            <Card>
              <BlockStack gap="400">
                <Text as="h3" variant="headingMd">Availability</Text>
                <InlineStack gap="400" wrap>
                  <Box minWidth="200px">
                    <Text as="p" variant="bodySm" tone="subdued">Install speed</Text>
                    <Box paddingBlockStart="100">
                      <InstallSpeedBadge
                        speed={(w as any).install_speed}
                        source={(w as any).source_guide}
                        fastestWarehouse={(w as any).fastest_available_warehouse}
                        arrivalDays={(w as any).estimated_arrival_days}
                        inventoryStatus={(w as any).inventory_status}
                        inventoryAgeHours={(w as any).inventory_age_hours}
                      />
                      {(w as any).fastest_available_warehouse && (
                        <Box paddingBlockStart="100">
                          <Text as="p" variant="bodySm" tone="subdued">
                            Ships from <strong>{(w as any).fastest_available_warehouse}</strong> via UPS Ground (~{(w as any).estimated_arrival_days} business days to 30062).
                          </Text>
                        </Box>
                      )}
                    </Box>
                  </Box>
                  <Box minWidth="140px">
                    <Text as="p" variant="bodySm" tone="subdued">Same-day (DC 121)</Text>
                    <Text as="p" variant="headingMd">{(w as any).qty_local ?? '—'}</Text>
                  </Box>
                  <Box minWidth="140px">
                    <Text as="p" variant="bodySm" tone="subdued">Next-day</Text>
                    <Text as="p" variant="headingMd">{(w as any).qty_local_plus ?? '—'}</Text>
                  </Box>
                  <Box minWidth="140px">
                    <Text as="p" variant="bodySm" tone="subdued">3-5 days</Text>
                    <Text as="p" variant="headingMd">{(w as any).qty_nationwide ?? '—'}</Text>
                  </Box>
                  <Box minWidth="120px">
                    <Text as="p" variant="bodySm" tone="subdued">Returnable</Text>
                    <Box paddingBlockStart="100">
                      {(w as any).is_returnable === false ? <Badge tone="critical">No</Badge>
                       : (w as any).is_returnable === true ? <Badge tone="success">Yes</Badge>
                       : <Text as="span" tone="subdued">—</Text>}
                    </Box>
                  </Box>
                </InlineStack>
                <Text as="p" variant="bodySm" tone="subdued">
                  Totals across all CONUS DCs. Hawaii / Alaska / Canada / PR excluded.
                  ATD same-day = DC 121 Kennesaw GA. WheelPros has no SFTP
                  inventory on this account — qty values come from a separate
                  pipeline; rows older than 24 hours are flagged as stale.
                </Text>
                <Divider />
                {/* Inventory provenance — surfaces inventory_source +
                    inventory_status + inventory_age_hours from the API so
                    operator can see exactly why a row is rendering as stale
                    / unknown / out_of_stock. Read-only diagnostic. */}
                <Text as="h3" variant="headingMd">Inventory provenance</Text>
                <InlineStack gap="400" wrap>
                  <Box minWidth="200px">
                    <Text as="p" variant="bodySm" tone="subdued">Status</Text>
                    <Box paddingBlockStart="100">
                      {(w as any).inventory_status === 'stale' ? <Badge tone="warning">Stale</Badge>
                       : (w as any).inventory_status === 'unknown' ? <Badge>Unknown</Badge>
                       : (w as any).inventory_status === 'fresh_oos' ? <Badge tone="critical">Out of stock</Badge>
                       : (w as any).inventory_status === 'fresh_in_stock' ? <Badge tone="success">In stock</Badge>
                       : <Text as="span" tone="subdued">—</Text>}
                    </Box>
                  </Box>
                  <Box minWidth="200px">
                    <Text as="p" variant="bodySm" tone="subdued">Source</Text>
                    <Text as="p" variant="bodyMd">{(w as any).inventory_source ?? '—'}</Text>
                  </Box>
                  <Box minWidth="200px">
                    <Text as="p" variant="bodySm" tone="subdued">Last inventory sync</Text>
                    <Text as="p" variant="bodyMd">{ago((w as any).last_inventory_sync_at)}</Text>
                  </Box>
                  <Box minWidth="200px">
                    <Text as="p" variant="bodySm" tone="subdued">Effective install speed</Text>
                    <Text as="p" variant="bodyMd">
                      {(w as any).effective_install_speed ?? '—'}
                      {(w as any).effective_install_speed !== (w as any).install_speed && (
                        <Text as="span" tone="subdued"> (clamped from {(w as any).install_speed ?? '—'})</Text>
                      )}
                    </Text>
                  </Box>
                </InlineStack>
                <Divider />
                <Text as="h3" variant="headingMd">Freshness</Text>
                <InlineStack gap="400" wrap>
                  <Box minWidth="160px">
                    <Text as="p" variant="bodySm" tone="subdued">Vendor feed</Text>
                    <Text as="p" variant="bodyMd">{ago(w.last_vendor_feed_at)}</Text>
                  </Box>
                  <Box minWidth="160px">
                    <Text as="p" variant="bodySm" tone="subdued">Imported</Text>
                    <Text as="p" variant="bodyMd">{ago(w.last_imported_at)}</Text>
                  </Box>
                  <Box minWidth="160px">
                    <Text as="p" variant="bodySm" tone="subdued">Pricing sync</Text>
                    <Text as="p" variant="bodyMd">{ago(w.last_pricing_sync_at)}</Text>
                  </Box>
                  <Box minWidth="160px">
                    <Text as="p" variant="bodySm" tone="subdued">Shopify push</Text>
                    <Text as="p" variant="bodyMd">{ago(w.last_shopify_push_at)}</Text>
                  </Box>
                  <Box minWidth="140px">
                    <Text as="p" variant="bodySm" tone="subdued">Image status</Text>
                    <Box paddingBlockStart="100">
                      {hasImage(w as any) ? <Badge tone="success">Has image</Badge> : <Badge tone="critical">No image</Badge>}
                    </Box>
                  </Box>
                </InlineStack>
              </BlockStack>
            </Card>
          </Layout.Section>

          <Layout.Section>
            <Card>
              <BlockStack gap="400">
                <Text as="h3" variant="headingMd">Parts + vendor</Text>
                <FormLayout>
                  <FormLayout.Group>
                    <TextField label="Source / vendor" value={(w as any).source_guide ?? ''} disabled autoComplete="off" />
                    <TextField label="Manufacturer part #" value={w.mfg_part_number ?? ''} onChange={handle('mfg_part_number' as any)} autoComplete="off" />
                    <TextField label="Vendor part #" value={(w as any).vendor_part_number ?? ''} disabled autoComplete="off" />
                    <TextField label="ATD part #" value={(w as any).atd_part_number ?? ''} disabled autoComplete="off" />
                  </FormLayout.Group>
                </FormLayout>
              </BlockStack>
            </Card>
          </Layout.Section>

          <Layout.Section>
            <Card>
              <BlockStack gap="400">
                <Text as="h3" variant="headingMd">Specs</Text>
                <FormLayout>
                  <FormLayout.Group>
                    <TextField label="Diameter (in)" type="number" step={0.1} value={numStr(w.diameter_in)} onChange={handleNum('diameter_in')} autoComplete="off" />
                    <TextField label="Width (in)" type="number" step={0.1} value={numStr(w.width_in)} onChange={handleNum('width_in')} autoComplete="off" />
                    <TextField label="Offset (mm)" type="number" value={numStr(w.offset_mm)} onChange={handleNum('offset_mm')} autoComplete="off" />
                    <TextField label="Backspacing (in)" type="number" step={0.01} value={numStr(w.backspacing_in)} onChange={handleNum('backspacing_in')} autoComplete="off" />
                  </FormLayout.Group>
                  <FormLayout.Group>
                    <TextField label="Hub bore (mm)" type="number" step={0.1} value={numStr(w.hub_bore_mm)} onChange={handleNum('hub_bore_mm')} autoComplete="off" />
                    <TextField label="Load rating (lbs)" type="number" value={numStr(w.load_rating_lbs)} onChange={handleNum('load_rating_lbs')} autoComplete="off" />
                  </FormLayout.Group>
                  <FormLayout.Group>
                    <Checkbox label="Dually" checked={w.is_dually ?? false} onChange={handle('is_dually')} />
                    <Checkbox label="Beadlock" checked={w.is_beadlock ?? false} onChange={handle('is_beadlock')} />
                  </FormLayout.Group>
                </FormLayout>
              </BlockStack>
            </Card>
          </Layout.Section>

          <Layout.Section>
            <WheelImageGallery
              wheel={wheel}
              form={form}
              onChangeField={(key, val) => setForm((p) => ({ ...p, [key]: val }))}
              onVerify={(v) => verify.mutate(v)}
              verifyLoading={verify.isPending}
            />
          </Layout.Section>

          <Layout.Section>
            <ListingSeoPanel kind="wheel" product={wheel as Wheel} />
          </Layout.Section>

          <Layout.Section>
            <GoogleMerchantPanel kind="wheel" product={wheel as Wheel} />
          </Layout.Section>

          <Layout.Section>
            <MetafieldsPanel kind="wheel" product={wheel as Wheel} />
          </Layout.Section>

          <Layout.Section>
            <VariantsPanel kind="wheel" product={wheel as Wheel} />
          </Layout.Section>

          <Layout.Section>
            <CollectionMembershipPreview wheelId={wheel!.id} />
          </Layout.Section>

          <Layout.Section>
            <Card>
              <BlockStack gap="300">
                <InlineStack align="space-between" blockAlign="center">
                  <BlockStack gap="050">
                    <Text as="h3" variant="headingMd">Shopify product listing</Text>
                    <Text as="p" variant="bodySm" tone="subdued">
                      Generate the full Shopify-ready JSON (title, SEO, description HTML, tags, metafields, image array, schema JSON-LD) from this wheel's data. Template-based today; upgrades to AI when ANTHROPIC_API_KEY is set.
                    </Text>
                  </BlockStack>
                  <Button
                    variant="primary"
                    loading={generate.isPending}
                    onClick={() => generate.mutate()}
                  >
                    Generate listing
                  </Button>
                </InlineStack>
                {listing && (
                  <Box background="bg-surface-secondary" padding="300" borderRadius="200">
                    <BlockStack gap="200">
                      <InlineStack gap="200" wrap>
                        {(listing.listing.warnings ?? []).map((w: string, i: number) => (
                          <Banner key={i} tone="warning">{w}</Banner>
                        ))}
                      </InlineStack>
                      <Text as="p" variant="bodySm" fontWeight="semibold">
                        {listing.listing.product_title}
                      </Text>
                      <Text as="p" variant="bodySm" tone="subdued">
                        Handle: {listing.listing.url_handle} · Tags: {(listing.listing.tags ?? []).length} · Images: {(listing.listing.images?.assets ?? []).length}
                      </Text>
                      <pre style={{ margin: 0, fontSize: 11, maxHeight: 400, overflow: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
{JSON.stringify(listing.listing, null, 2)}
                      </pre>
                    </BlockStack>
                  </Box>
                )}
                {generate.error && (
                  <Banner tone="critical" title="Listing generation failed">
                    <p>{(generate.error as Error).message || 'Unknown error — check the API logs.'}</p>
                    <p style={{ fontSize: 12, marginTop: 8, opacity: 0.85 }}>
                      Wheel ID {numericId}. If the message mentions Anthropic or the API key,
                      AI enhancement is the cause; the template-only listing still works without it.
                    </p>
                  </Banner>
                )}
              </BlockStack>
            </Card>
          </Layout.Section>

          <Layout.Section>
            <AuditLogPanel entityTable="wheel_products" entityId={numericId ?? undefined} />
          </Layout.Section>
        </Layout>
        {toast && <Toast content={toast} onDismiss={() => setToast(null)} />}
      </Page>
    </>
  );
}

function numStr(n: number | null | undefined): string {
  return n === null || n === undefined ? '' : String(n);
}

function buildPatch(form: Partial<Wheel> & { bolt_pattern_raw?: string }): WheelPayload {
  const {
    id: _id, is_active, created_at, updated_at, bolt_patterns, bolt_patterns_canonical,
    images_verified, images_verified_at, images_verified_by,
    ...patch
  } = form as any;
  return patch;
}

function CollectionMembershipPreview({ wheelId }: { wheelId: number }) {
  const q = useQuery({
    queryKey: ['wheel-tags', wheelId],
    queryFn: () => getTagsForWheel(wheelId),
  });
  if (q.isLoading) return <Card><Spinner /></Card>;
  if (q.error) return <Card><Banner tone="critical">{(q.error as Error).message}</Banner></Card>;
  const tags = q.data?.tags ?? [];

  // Group tags by their key (everything before the first colon).
  const groups: Record<string, string[]> = {};
  for (const t of tags) {
    const i = t.indexOf(':');
    const key = i > 0 ? t.slice(0, i) : t;
    const value = i > 0 ? t.slice(i + 1) : '';
    if (!groups[key]) groups[key] = [];
    groups[key].push(value);
  }

  // Map each tag-key to its human-readable collection group name + sample
  // collection name. The collection name follows the same pattern as the
  // global preview page.
  function describe(key: string, value: string): { group: string; collection: string } | null {
    if (key === 'type') return null; // not a collection rule
    if (key === 'source') return null; // internal-only tag
    if (key === 'brand') return { group: 'Wheels by Brand', collection: `${prettify(value)} Wheels` };
    if (key === 'diameter') return { group: 'Wheels by Diameter', collection: `${value}\" Wheels` };
    if (key === 'width') return { group: 'Wheels by Width', collection: `Width ${value.replace('-','.')}\"` };
    if (key === 'bolt') return { group: 'Wheels by Bolt Pattern', collection: `${prettify(value)} Bolt Pattern Wheels` };
    if (key === 'finish') return { group: 'Wheels by Finish', collection: `${prettify(value)} Wheels` };
    if (key === 'vehicle_type') return { group: 'Wheels by Vehicle Type', collection: `${prettify(value)} Wheels` };
    if (key === 'install_speed') return { group: 'Wheels by Install Speed', collection: `${prettify(value).replace('_',' ')} Install` };
    if (key === 'fits-make') return { group: 'Wheels by Fitment / Vehicle', collection: `Wheels for ${prettify(value)}` };
    if (key === 'fits') return { group: 'Wheels by Fitment / Vehicle', collection: `Wheels for ${prettify(value)}` };
    return { group: 'Other', collection: `${key}:${value}` };
  }
  function prettify(s: string): string {
    return s.split('-').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  }

  const memberships: Array<{ group: string; collection: string; tag: string }> = [];
  for (const [key, vals] of Object.entries(groups)) {
    for (const v of vals) {
      const d = describe(key, v);
      if (d) memberships.push({ ...d, tag: `${key}:${v}` });
    }
  }
  // Group by group-name for rendering.
  const byGroup: Record<string, typeof memberships> = {};
  for (const m of memberships) {
    if (!byGroup[m.group]) byGroup[m.group] = [];
    byGroup[m.group].push(m);
  }

  return (
    <Card>
      <BlockStack gap="300">
        <InlineStack align="space-between" blockAlign="center">
          <BlockStack gap="050">
            <Text as="h3" variant="headingMd">Collection Membership Preview</Text>
            <Text as="p" variant="bodySm" tone="subdued">
              Smart Collections this wheel will join when products go live, based on its
              tag set. Read-only — no collections have been created yet.
            </Text>
          </BlockStack>
          <Badge tone="info">{`${memberships.length} memberships`}</Badge>
        </InlineStack>

        <BlockStack gap="200">
          {Object.entries(byGroup).map(([group, items]) => (
            <Box key={group} background="bg-surface-secondary" padding="300" borderRadius="200">
              <BlockStack gap="100">
                <Text as="span" variant="bodyMd" fontWeight="semibold">{group}</Text>
                {items.map((m, i) => (
                  <InlineStack key={i} gap="200" blockAlign="center">
                    <Badge tone="success">{m.collection}</Badge>
                    <Text as="span" variant="bodySm" tone="subdued">
                      <code style={{ fontSize: 11 }}>{m.tag}</code>
                    </Text>
                  </InlineStack>
                ))}
              </BlockStack>
            </Box>
          ))}
        </BlockStack>

        <Banner tone="info">
          No collections have been created in Shopify yet. This is preview only.
          Once collections are reviewed and approved, smart-collection rules will be
          created from the tag taxonomy.
        </Banner>
      </BlockStack>
    </Card>
  );
}

